// What the forecast header offers to do next: which triggers to explore as a scenario, whether
// "Update now" is open, and how the update cadence reads. Pure (no React).

import { triggerImpact } from "../report/brief.js";
import { triggerWhenOf } from "./reportAnalysis.js";
import { isConcluded } from "../tracking.js";
import { awaitingAnswer } from "./answerCheck.js";
import { CADENCES } from "../scheduleMap.js";
import { laneTier, rootEngine } from "./engines.js";
import { isSayerManaged } from "../featured/observerCopy.js";
import { canEdit, canUpdateNow, isReadOnlyRow } from "./sharedRowGuards.js";
import { lacksDetail } from "./detailBackfill.js";
import { latestForecastPointer, parseFireTs } from "./reportPointer.js";

/**
 * The forecaster of the forecast's most recent run: its latest saved fire (scheduled or on
 * demand), unless a manual update recorded on the row itself is newer. Null when unknown.
 * @param {object|null} run the forecast row
 * @returns {string|null}
 */
export function lastRunEngine(run) {
  if (!run) return null;
  const own = rootEngine(run);
  // Fires are ordered by when they ran (`fire_ts`), as the live report orders them, not by when
  // they were saved (`at`), which a late delivery puts out of order.
  const fires = (run.reportPointers || []).filter(
    (p) => p?.forecaster && (p.kind ?? "forecast") === "forecast" && (p.fire_ts || p.at),
  );
  const latest = latestForecastPointer(fires.map((p) => ({ ...p, kind: "forecast" })));
  if (!latest) return own;
  const firedMs = parseFireTs(latest.fire_ts);
  const latestMs = Number.isFinite(firedMs) ? firedMs : Date.parse(latest.at);
  const hist = Array.isArray(run.history) ? run.history : [];
  const last = hist.length ? hist[hist.length - 1] : null;
  // A history entry's `snapshotAt` is the update itself; its `generatedAt` dates the version the
  // update replaced, and is all a legacy entry carries.
  const manualMs = Date.parse(last?.snapshotAt ?? last?.generatedAt);
  if (own && manualMs > latestMs) return own;
  return latest.forecaster;
}

/**
 * The depth Update now runs at unless the user picks another: the depth of the forecast's latest
 * version, so a Deep forecast stays Deep. Null when that version's forecaster serves no depth.
 * @param {object|null} run the forecast row
 * @returns {"quick"|"deep"|null}
 */
export function updateNowTier(run) {
  return laneTier(lastRunEngine(run));
}

/**
 * When the forecast was last updated, for the cooldown: the newest of its creation, this tab's
 * `lastReforecastedAt`, and its saved history entries' `snapshotAt`. The server stores no
 * `lastReforecastedAt`, so after a reload a manual update survives only as its history entry.
 * @param {object} run the forecast row
 * @returns {number} ms, or NaN when none is a valid date
 */
export function lastUpdateMs(run) {
  const history = Array.isArray(run?.history) ? run.history : [];
  const moments = [run?.createdAt, run?.lastReforecastedAt, ...history.map((h) => h?.snapshotAt)]
    .map((m) => (typeof m === "string" ? Date.parse(m) : NaN))
    .filter(Number.isFinite);
  return moments.length ? Math.max(...moments) : NaN;
}

/** The manual-update cooldown the timeline pill always applied. */
export const UPDATE_COOLDOWN_MS = 30 * 60 * 1000;
/** Why Update now is greyed: a run in progress or the cooldown after one. */
const COOLDOWN_REASON = "Available 30 minutes after the last reforecast";
const LOADING_REASON = "Available once this forecast has loaded";

const localDay = (d) =>
  [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");

/**
 * How much a trigger would move the answer: the report's own measure (`triggerImpact`: the gap
 * between the two conditional answers, or the distribution distance), falling back to the
 * weights the timeline sizes its nodes by (`dist_impact`, `p_conditional`, `relevance`).
 */
export function triggerWeight(ev) {
  const impact = triggerImpact(ev);
  if (impact > 0) return impact;
  const fallback = ev?.dist_impact ?? ev?.p_conditional ?? ev?.relevance;
  return typeof fallback === "number" && Number.isFinite(fallback) ? fallback : 0;
}

/**
 * The upcoming triggers worth exploring as a scenario, strongest first. "Upcoming" is the
 * report's rule: not past, and dated today or later at the date's own precision ("2026-10" is
 * upcoming all October). If none survive the date test, every not-past trigger is offered.
 * @param {Array} events the forecast's triggers
 * @param {{today?: Date, limit?: number}} [opts]
 * @returns {Array<{ev: object, weight: number, when: string, pEvent: number|null}>}
 */
export function scenarioTriggers(events, { today = new Date(), limit = 5 } = {}) {
  const todayIso = localDay(today);
  const live = (Array.isArray(events) ? events : []).filter(
    (ev) => ev && !ev.isPast && !ev._isTrigger && typeof ev.title === "string" && ev.title,
  );
  const dated = live.filter((ev) => {
    const when = triggerWhenOf(ev).date;
    return !!when && when >= todayIso.slice(0, when.length);
  });
  const pool = dated.length ? dated : live;
  return pool
    .map((ev) => ({
      ev,
      weight: triggerWeight(ev),
      when: triggerWhenOf(ev).date || "",
      pEvent: typeof ev.p_event === "number" && Number.isFinite(ev.p_event) ? ev.p_event : null,
    }))
    .sort((a, b) => b.weight - a.weight || (a.when < b.when ? -1 : 1))
    .slice(0, limit);
}

/**
 * Whether "Update now" shows and can be pressed: a saved, live (not scrubbed), not-yet-concluded
 * forecast whose question has not closed (a closed one offers "Check now" instead) under forecast
 * tracking that the viewer may edit (`canUpdateNow`: the owner or an
 * in-org collaborator); the 30-minute cooldown (`lastUpdateMs`) disables rather than hides it,
 * with the reason as its tooltip, and so does a run in progress (`running`) or a forecast whose
 * detail has not loaded yet. On another
 * organization's forecast the slot offers a forecast of the viewer's own instead (`runAsMine`).
 * @param {{run: any, forecastTracking: boolean, scrubbed: boolean, nowMs: number}} args
 * @returns {{visible: boolean, enabled: boolean, reason: string|null, runAsMine?: boolean,
 *   running?: boolean}}
 */
export function updateNowState({ run, forecastTracking, scrubbed, nowMs }) {
  const hidden = { visible: false, enabled: false, reason: null };
  if (!forecastTracking || !run || !run.createdAt) return hidden;
  if (scrubbed || isConcluded(run) || awaitingAnswer(run, nowMs)) return hidden;
  const sharedWithViewer = isReadOnlyRow(run);
  if (sharedWithViewer && !canEdit(run)) {
    if (run._pending) return hidden;
    return {
      visible: true,
      enabled: true,
      reason: "This forecast belongs to another organization. Run your own forecast of it.",
      runAsMine: true,
    };
  }
  if (!canUpdateNow(run)) return hidden;
  if (run._pending) {
    return {
      visible: true,
      enabled: false,
      reason: COOLDOWN_REASON,
      running: true,
    };
  }
  // A rerun builds on the forecast's history and events, which a lean row does not hold yet.
  if (lacksDetail(run)) {
    return { visible: true, enabled: false, reason: LOADING_REASON };
  }
  // Updating a copy of one of Sayer's forecasts first makes it the user's own.
  if (isSayerManaged(run)) {
    return {
      visible: true,
      enabled: true,
      reason: "Update with fresh research. This makes it your own forecast.",
    };
  }
  const last = lastUpdateMs(run);
  if (Number.isFinite(last) && nowMs - last <= UPDATE_COOLDOWN_MS) {
    return { visible: true, enabled: false, reason: COOLDOWN_REASON };
  }
  return {
    visible: true,
    enabled: true,
    reason: sharedWithViewer
      ? "Update this forecast with fresh research for everyone it's shared with"
      : "Update this forecast with fresh research",
  };
}

const CADENCE_TEXT = {
  Never: "Updates off",
  "On Triggers": "Updates on triggers",
  Hourly: "Updates hourly",
  Daily: "Updates daily",
  Weekly: "Updates weekly",
  Monthly: "Updates monthly",
};

/**
 * "Updates weekly" for a tracking-cadence index (`getTrackingPrefs(id).cadIdx`), or null.
 * @param {number|null|undefined} cadIdx
 */
export function cadenceText(cadIdx) {
  if (typeof cadIdx !== "number") return null;
  const label = CADENCES[cadIdx]?.label;
  return label ? (CADENCE_TEXT[label] ?? `Updates ${label.toLowerCase()}`) : null;
}

/**
 * The schedule line a forecast shows: "Updates weekly", or "Updates off" for a copy of one of
 * Sayer's forecasts, which keeps Sayer's schedule; null where tracking is off (`cadIdx` null).
 * @param {object|null} run the forecast row
 * @param {number|null|undefined} cadIdx
 */
export function cadenceLabel(run, cadIdx) {
  return isSayerManaged(run) && cadIdx != null ? "Updates off" : cadenceText(cadIdx);
}
